/**
 * SessionExpiredOverlay - silent-retry-then-interactive auth recovery.
 *
 * Mounted once at the root layout. Subscribes to `session-expired` events
 * (emitted by api.ts/websocket-service/game-connection-manager when an auth
 * call returns 401/4401/4403). On each event:
 *
 *   1. Try ONE silent `auth.currentUser.getIdToken(true)`. If it succeeds,
 *      the next API/WS call gets the fresh token and the user never sees
 *      this overlay.
 *   2. If silent fails, render interactive UI with Retry + Sign-out CTAs.
 *      "Retry" repeats step 1; "Sign out and sign in" calls the regular
 *      auth-service signOut() (the only signOut path we still allow).
 *
 * Per Inv 3, NO code path in this overlay calls `auth.signOut()` itself —
 * only the user's explicit click runs the existing user-initiated path.
 *
 * One silent retry per mount. If the user is offline or Firebase is down,
 * the overlay STAYS interactive and waits for a click rather than looping.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, RefreshCw, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { auth } from '@/lib/firebase';
import { signOut as authServiceSignOut } from '@/lib/auth-service';
import { onSessionExpired } from '@/lib/session-expired-events';
import { Button } from '@/components/ui/button';

type OverlayState =
  | { status: 'hidden' }
  | { status: 'silent-retry' }
  | { status: 'interactive'; error?: string };

export function SessionExpiredOverlay() {
  const [state, setState] = useState<OverlayState>({ status: 'hidden' });
  const navigate = useNavigate();
  // Cancellation token for an in-flight silent retry — flips to true if the
  // user clicks before the silent attempt resolves.
  const cancelRef = useRef<{ cancelled: boolean } | null>(null);
  // Mirror `state.status` into a ref so the event subscription can read the
  // freshest value WITHOUT putting `state.status` in the effect deps. Otherwise
  // every status flip (e.g. hidden → silent-retry) tears down the subscription
  // and re-creates it on the next render, leaving a tiny window where a
  // session-expired event can fire and be dropped entirely.
  const statusRef = useRef<OverlayState['status']>(state.status);
  statusRef.current = state.status;

  const startSilentRetry = useCallback(async (): Promise<{ ok: boolean; cancelled: boolean }> => {
    const token = { cancelled: false };
    cancelRef.current = token;
    setState({ status: 'silent-retry' });

    try {
      const user = auth.currentUser;
      if (!user) {
        if (token.cancelled) return { ok: false, cancelled: true };
        return { ok: false, cancelled: false };
      }
      await user.getIdToken(/* forceRefresh */ true);
      if (token.cancelled) return { ok: false, cancelled: true };
      setState({ status: 'hidden' });
      return { ok: true, cancelled: false };
    } catch {
      if (token.cancelled) return { ok: false, cancelled: true };
      return { ok: false, cancelled: false };
    } finally {
      if (cancelRef.current === token) cancelRef.current = null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onSessionExpired(async () => {
      // Already showing or already retrying — coalesce duplicate emissions.
      // Read the freshest value via ref so we don't have to re-bind the
      // subscription on every status change.
      if (statusRef.current !== 'hidden') return;
      const result = await startSilentRetry();
      // If a user click cancelled this attempt, the click handler now owns
      // the UI state — do NOT setState('interactive') here, or we'd flash
      // the overlay back to interactive after a successful manual retry
      // already set 'hidden'.
      if (result.cancelled) return;
      if (!result.ok) {
        setState({ status: 'interactive' });
      }
    });
    return unsubscribe;
  }, [startSilentRetry]);

  const handleRetryClick = useCallback(async () => {
    // Cancel any silent retry in flight (per plan: user click cancels).
    if (cancelRef.current) cancelRef.current.cancelled = true;
    const result = await startSilentRetry();
    if (result.cancelled) return;
    if (!result.ok) {
      setState({
        status: 'interactive',
        error: 'Could not refresh your session. Try signing out and signing in again.',
      });
    }
  }, [startSilentRetry]);

  const handleSignOut = useCallback(async () => {
    if (cancelRef.current) cancelRef.current.cancelled = true;
    setState({ status: 'hidden' });
    try {
      await authServiceSignOut();
    } finally {
      navigate('/login');
    }
  }, [navigate]);

  if (state.status !== 'interactive') return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6 max-w-md px-6"
      >
        <motion.div
          className="mx-auto w-24 h-24 rounded-full bg-amber-500/10 flex items-center justify-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ShieldAlert className="h-12 w-12 text-amber-500" />
        </motion.div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">Session Expired</h2>
          <p className="text-muted-foreground">
            We couldn&apos;t refresh your session automatically. Try again, or sign out and sign
            back in.
          </p>
        </div>

        {state.error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-destructive"
          >
            {state.error}
          </motion.p>
        )}

        <div className="space-y-3">
          <Button onClick={handleRetryClick} size="lg" className="w-full max-w-xs">
            <RefreshCw className="mr-2 h-5 w-5" />
            Retry
          </Button>
          <Button onClick={handleSignOut} variant="ghost" size="lg" className="w-full max-w-xs">
            <LogOut className="mr-2 h-5 w-5" />
            Sign out and sign in
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
